import React from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { EightDReport, D6PreventiveAction } from '../types/eightd.types';

// D6: Preventive Actions
export const D6PreventiveActions: React.FC<{
    report: Partial<EightDReport>;
    updateField: (path: string, value: string | number | boolean | object | null | undefined) => void;
    setReport: React.Dispatch<React.SetStateAction<Partial<EightDReport>>>;
}> = ({ report, setReport }) => {
    const addAction = () => {
        const newAction: D6PreventiveAction = {
            id: `pa-${Date.now()}`,
            description: '',
            scope: 'This Product',
            responsibility: '',
            responsibilityName: '',
            dueDate: new Date(),
            status: 'Planned',
            implementation: '',
        };
        setReport(prev => ({
            ...prev,
            d6_preventive: [...(prev.d6_preventive || []), newAction],
        }));
    };

    const updateAction = (id: string, field: string, value: string | number | boolean | Date) => {
        setReport(prev => ({
            ...prev,
            d6_preventive: prev.d6_preventive?.map(action =>
                action.id === id ? { ...action, [field]: value } : action
            ),
        }));
    };

    const removeAction = (id: string) => {
        setReport(prev => ({
            ...prev,
            d6_preventive: prev.d6_preventive?.filter(action => action.id !== id),
        }));
    };

    return (
        <div className="space-y-6">
            <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">D6: Implement Preventive Actions</h3>
                <p className="text-sm text-gray-600 mb-6">
                    Modify systems and processes to prevent recurrence across all products and locations
                </p>
            </div>

            <div className="flex justify-between items-center">
                <h4 className="font-medium text-gray-900">Preventive Actions</h4>
                <button
                    onClick={addAction}
                    className="flex items-center gap-2 px-3 py-1 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                    <Plus className="w-4 h-4" />
                    Add Action
                </button>
            </div>

            {report.d6_preventive && report.d6_preventive.length > 0 ? (
                <div className="space-y-4">
                    {report.d6_preventive.map((action, index) => (
                        <div key={action.id} className="p-4 border border-gray-200 rounded-lg">
                            <div className="flex items-start justify-between mb-4">
                                <h5 className="font-medium text-gray-900">Preventive Action #{index + 1}</h5>
                                <button
                                    onClick={() => removeAction(action.id)}
                                    className="text-red-600 hover:text-red-700"
                                >
                                    <Trash2 className="w-4 h-4" />
                                </button>
                            </div>

                            <div className="grid md:grid-cols-2 gap-4">
                                <div className="md:col-span-2">
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Preventive Action Description *
                                    </label>
                                    <textarea
                                        value={action.description}
                                        onChange={(e) => updateAction(action.id, 'description', e.target.value)}
                                        rows={2}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        placeholder="Describe the preventive action to prevent recurrence..."
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Scope
                                    </label>
                                    <select
                                        value={action.scope}
                                        onChange={(e) => updateAction(action.id, 'scope', e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    >
                                        <option value="This Product">This Product</option>
                                        <option value="Product Family">Product Family</option>
                                        <option value="All Products">All Products</option>
                                        <option value="Company-wide">Company-wide</option>
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Responsible Person
                                    </label>
                                    <input
                                        type="text"
                                        value={action.responsibilityName}
                                        onChange={(e) => updateAction(action.id, 'responsibilityName', e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Due Date
                                    </label>
                                    <input
                                        type="date"
                                        value={action.dueDate instanceof Date ? action.dueDate.toISOString().split('T')[0] : ''}
                                        onChange={(e) => updateAction(action.id, 'dueDate', new Date(e.target.value))}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Status
                                    </label>
                                    <select
                                        value={action.status}
                                        onChange={(e) => updateAction(action.id, 'status', e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    >
                                        <option value="Planned">Planned</option>
                                        <option value="In Progress">In Progress</option>
                                        <option value="Completed">Completed</option>
                                    </select>
                                </div>

                                <div className="md:col-span-2">
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Implementation Details
                                    </label>
                                    <textarea
                                        value={action.implementation}
                                        onChange={(e) => updateAction(action.id, 'implementation', e.target.value)}
                                        rows={2}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        placeholder="How will this be implemented across the defined scope?"
                                    />
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            ) : (
                <div className="p-8 bg-gray-50 border border-gray-200 rounded-lg text-center">
                    <p className="text-gray-600">No preventive actions added yet</p>
                    <button
                        onClick={addAction}
                        className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                        Add First Action
                    </button>
                </div>
            )}
        </div>
    );
};

// D7: Team Recognition
export const D7Recognition: React.FC<{
    report: Partial<EightDReport>;
    updateField: (path: string, value: string | number | boolean | object | null | undefined) => void
}> = ({ report, updateField }) => {
    return (
        <div className="space-y-6">
            <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">D7: Congratulate the Team</h3>
                <p className="text-sm text-gray-600 mb-6">
                    Recognize team contributions, document lessons learned, and share best practices
                </p>
            </div>

            <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                    Team Accomplishments *
                </label>
                <textarea
                    value={report.d7_recognition?.teamAccomplishments || ''}
                    onChange={(e) => updateField('d7_recognition.teamAccomplishments', e.target.value)}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="What did the team achieve? What went well?"
                />
            </div>

            <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                    Lessons Learned *
                </label>
                <textarea
                    value={report.d7_recognition?.lessonsLearned || ''}
                    onChange={(e) => updateField('d7_recognition.lessonsLearned', e.target.value)}
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="What did the organization learn from this problem? What would you do differently?"
                />
            </div>

            <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                    Best Practices *
                </label>
                <textarea
                    value={report.d7_recognition?.bestPractices || ''}
                    onChange={(e) => updateField('d7_recognition.bestPractices', e.target.value)}
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="What best practices emerged that should be shared across the organization?"
                />
            </div>

            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-900">
                    💡 <strong>Tip:</strong> Use this discipline to capture organizational learning and motivate continuous improvement.
                    Share these lessons learned in training, team meetings, and knowledge management systems.
                </p>
            </div>
        </div>
    );
};

// D8: Closure
export const D8Closure: React.FC<{
    report: Partial<EightDReport>;
    updateField: (path: string, value: string | number | boolean | object | null | undefined) => void
}> = ({ report, updateField }) => {
    return (
        <div className="space-y-6">
            <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">D8: Close the 8D</h3>
                <p className="text-sm text-gray-600 mb-6">
                    Document the final results, verify all actions are complete, and close the 8D report
                </p>
            </div>

            <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                    Closure Summary *
                </label>
                <textarea
                    value={report.d8_closure?.closureSummary || ''}
                    onChange={(e) => updateField('d8_closure.closureSummary', e.target.value)}
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Summarize the entire 8D process and final outcome..."
                />
            </div>

            <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                    Results Achieved *
                </label>
                <textarea
                    value={report.d8_closure?.resultsAchieved || ''}
                    onChange={(e) => updateField('d8_closure.resultsAchieved', e.target.value)}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="What specific results were achieved? Problem eliminated? Process improved?"
                />
            </div>

            <div>
                <h4 className="font-medium text-gray-900 mb-4">Metrics Improvement</h4>
                <div className="grid md:grid-cols-3 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Before: Defect Rate (%)
                        </label>
                        <input
                            type="number"
                            step="0.01"
                            value={report.d8_closure?.metricsImprovement?.beforeDefectRate || 0}
                            onChange={(e) => updateField('d8_closure.metricsImprovement.beforeDefectRate', parseFloat(e.target.value))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            After: Defect Rate (%)
                        </label>
                        <input
                            type="number"
                            step="0.01"
                            value={report.d8_closure?.metricsImprovement?.afterDefectRate || 0}
                            onChange={(e) => updateField('d8_closure.metricsImprovement.afterDefectRate', parseFloat(e.target.value))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Cost Savings ($)
                        </label>
                        <input
                            type="number"
                            value={report.d8_closure?.metricsImprovement?.costSavings || 0}
                            onChange={(e) => updateField('d8_closure.metricsImprovement.costSavings', parseFloat(e.target.value))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                    </div>
                </div>
            </div>

            <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                    Customer Satisfaction Improvement
                </label>
                <textarea
                    value={report.d8_closure?.metricsImprovement?.customerSatisfactionImprovement || ''}
                    onChange={(e) => updateField('d8_closure.metricsImprovement.customerSatisfactionImprovement', e.target.value)}
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="How did customer satisfaction improve?"
                />
            </div>

            <div className="pt-4 border-t border-gray-200">
                <label className="flex items-center gap-2 cursor-pointer">
                    <input
                        type="checkbox"
                        checked={report.d8_closure?.documentationComplete || false}
                        onChange={(e) => updateField('d8_closure.documentationComplete', e.target.checked)}
                        className="w-5 h-5 rounded"
                    />
                    <span className="font-medium text-gray-900">
                        All documentation is complete and archived
                    </span>
                </label>
            </div>

            <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-sm text-green-900">
                    ✅ <strong>Ready to Close:</strong> Verify that all 8 disciplines are complete,
                    all actions are implemented and verified, and the problem is eliminated before closing this report.
                </p>
            </div>
        </div>
    );
};
